import {
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { Loader2, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useDeleteNote } from '~entities/notes/api/delete-note';
import { useGetAllNotes } from '~entities/notes/api/get-all-notes';
import { type Note, NoteEntityType } from '~entities/notes/model/note';

import { AddEditNote } from '~features/notes/ui/add-edit-note';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table/header';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Textarea } from '~shared/shadcn/ui/textarea';

const columnHelper = createColumnHelper<Note>();

interface NotesTableProps {
  entityId: string;
}

export const NotesTable = ({ entityId }: NotesTableProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor('createdAt', {
        header: (info) => (
          <DataTableColumnHeader
            className='w-32 pl-4 text-black'
            column={info.column}
            title='Date'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='w-32 px-4'>
            {format(new Date(info.getValue()), 'MMMM d, yyyy')}
          </div>
        ),
      }),
      columnHelper.accessor('note', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Note'
            disableHiding
          />
        ),
        cell: (info) => {
          const noteText = info.getValue();
          const truncatedText =
            noteText.length > 50 ? `${noteText.substring(0, 50)}...` : noteText;
          return (
            <div className='px-4'>
              <Dialog>
                <DialogTrigger>{truncatedText}</DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Note</DialogTitle>
                  </DialogHeader>
                  <Textarea
                    placeholder='Enter your note...'
                    value={info.getValue()}
                    rows={20}
                    readOnly
                  />
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant={'outline'}>Close</Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'action',
        header: () => <div className='pl-4 text-black'>Actions</div>,
        cell: (info) => {
          return (
            <div className='flex items-center gap-2 px-4'>
              <AddEditNote
                note={info.row.original.note}
                noteId={info.row.original._id}
              />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant={'destructive'}
                    className='size-6 p-0 [&_svg]:size-3'
                  >
                    <Trash2 />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Note</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this note, you can&apos;t
                      revert this action
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDeleteNote(info.row.original._id)}
                    >
                      {deleteNoteMutation.isPending ? (
                        <div className='flex gap-2'>
                          <Loader2 className='animate-spin' /> Deleting
                        </div>
                      ) : (
                        <div>Delete</div>
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          );
        },
      }),
    ],
    [],
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const deleteNoteMutation = useDeleteNote();

  const { data, isFetching } = useGetAllNotes({
    entityId: entityId,
    entityType: NoteEntityType.Student,
    limit: pagination.pageSize,
    page: pagination.pageIndex + 1,
  });

  const handleDeleteNote = async (noteId: string) => {
    try {
      await deleteNoteMutation.mutateAsync({ noteId });
      toast.success('Note deleted successfully');
    } catch (error) {
      console.error(error);
      toast.error('Some error occurred while deleting Note');
    }
  };

  return (
    <EnrollioTable
      ariaLabel='Student notes'
      totalLabel='Notes'
      filterHeader={(table) => (
        <EnrollioTableSearch
          value={
            (table.getColumn('note')?.getFilterValue() as string | undefined) ??
            ''
          }
          onChange={(value) => {
            table.getColumn('note')?.setFilterValue(value);
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
          placeholder='Search notes...'
        />
      )}
      toolbar={<AddEditNote entityId={entityId} />}
      columns={columns}
      data={data?.notes ?? []}
      manualPagination
      rowCount={data?.total ?? 0}
      pagination={pagination}
      isUpdating={isFetching}
      onPaginationChange={(updater) => {
        setPagination((prev) => {
          const next = typeof updater === 'function' ? updater(prev) : updater;
          if (next.pageSize !== prev.pageSize) {
            return { ...next, pageIndex: 0 };
          }
          return next;
        });
      }}
    />
  );
};
